import { Injectable, inject, signal } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { AuthSessionService } from '../../../shell/auth/auth-session.service';
import type { OutboxOp, OutboxPhotoUploadOp, WoOutboxEntry } from './wo-offline.models';

const LEGACY_OUTBOX_KEY = 'wo_offline_outbox_v1';
const OUTBOX_KEY_PREFIX = 'wo_offline_outbox_v2:';
const DEAD_LETTER_KEY_PREFIX = 'wo_offline_deadletter_v1:';

@Injectable({ providedIn: 'root' })
export class WoOfflineOutboxService {
  private readonly auth = inject(AuthSessionService);

  /** Work order IDs with a pending submit queue for the current user. */
  readonly pendingWorkOrderIds = signal<ReadonlySet<number>>(new Set());
  readonly deadLetterWorkOrderIds = signal<ReadonlySet<number>>(new Set());

  async hydrate(): Promise<void> {
    const entries = await this.list();
    const dead = await this.listDeadLetter();
    this.pendingWorkOrderIds.set(new Set(entries.map((e) => e.workOrderId)));
    this.deadLetterWorkOrderIds.set(new Set(dead.map((e) => e.workOrderId)));
  }

  async list(): Promise<WoOutboxEntry[]> {
    return this.readStore(this.outboxKey());
  }

  async listDeadLetter(): Promise<WoOutboxEntry[]> {
    return this.readStore(this.deadLetterKey());
  }

  async get(workOrderId: number): Promise<WoOutboxEntry | null> {
    const entries = await this.list();
    return entries.find((e) => e.workOrderId === workOrderId) ?? null;
  }

  async getDeadLetter(workOrderId: number): Promise<WoOutboxEntry | null> {
    const entries = await this.listDeadLetter();
    return entries.find((e) => e.workOrderId === workOrderId) ?? null;
  }

  hasPending(workOrderId: number): boolean {
    return this.pendingWorkOrderIds().has(workOrderId);
  }

  hasDeadLetter(workOrderId: number): boolean {
    return this.deadLetterWorkOrderIds().has(workOrderId);
  }

  async enqueueCompletion(
    workOrderId: number,
    organizationId: number,
    projectId: number,
    photoOps: OutboxPhotoUploadOp[],
    completionComment?: string | null,
  ): Promise<void> {
    const userId = this.requireUserId();
    const ops: OutboxOp[] = [...photoOps, { type: 'SUBMIT' }];
    const entry: WoOutboxEntry = {
      workOrderId,
      organizationId,
      projectId,
      userId,
      createdAt: new Date().toISOString(),
      ops,
      completionComment: completionComment ?? null,
      lastError: null,
    };
    const entries = await this.list();
    const next = entries.filter((e) => e.workOrderId !== workOrderId);
    next.push(entry);
    await this.persistOutbox(next);
    await this.removeDeadLetter(workOrderId);
  }

  async replaceEntry(entry: WoOutboxEntry): Promise<void> {
    const entries = await this.list();
    const next = entries.filter((e) => e.workOrderId !== entry.workOrderId);
    next.push(entry);
    await this.persistOutbox(next);
  }

  async remove(workOrderId: number): Promise<void> {
    const entries = await this.list();
    await this.persistOutbox(entries.filter((e) => e.workOrderId !== workOrderId));
  }

  async setLastError(workOrderId: number, message: string | null): Promise<void> {
    const entries = await this.list();
    const idx = entries.findIndex((e) => e.workOrderId === workOrderId);
    if (idx < 0) {
      return;
    }
    entries[idx] = { ...entries[idx], lastError: message };
    await this.persistOutbox(entries);
  }

  async moveToDeadLetter(entry: WoOutboxEntry, message: string): Promise<void> {
    const dead: WoOutboxEntry = {
      ...entry,
      lastError: message,
      deadLetteredAt: new Date().toISOString(),
    };
    const letters = await this.listDeadLetter();
    const next = letters.filter((e) => e.workOrderId !== entry.workOrderId);
    next.push(dead);
    await this.persistDeadLetter(next);
    await this.remove(entry.workOrderId);
  }

  async retryFromDeadLetter(workOrderId: number): Promise<WoOutboxEntry | null> {
    const dead = await this.getDeadLetter(workOrderId);
    if (!dead) {
      return null;
    }
    const revived: WoOutboxEntry = {
      ...dead,
      lastError: null,
      deadLetteredAt: null,
    };
    await this.replaceEntry(revived);
    await this.removeDeadLetter(workOrderId);
    return revived;
  }

  async discardDeadLetter(workOrderId: number): Promise<void> {
    await this.removeDeadLetter(workOrderId);
  }

  private async removeDeadLetter(workOrderId: number): Promise<void> {
    const letters = await this.listDeadLetter();
    await this.persistDeadLetter(letters.filter((e) => e.workOrderId !== workOrderId));
  }

  private async readStore(key: string | null): Promise<WoOutboxEntry[]> {
    if (!key) {
      return [];
    }
    const { value } = await Preferences.get({ key });
    if (!value) {
      return [];
    }
    try {
      const parsed = JSON.parse(value) as WoOutboxEntry[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private async persistOutbox(entries: WoOutboxEntry[]): Promise<void> {
    const key = this.outboxKey();
    if (!key) {
      return;
    }
    await Preferences.set({ key, value: JSON.stringify(entries) });
    this.pendingWorkOrderIds.set(new Set(entries.map((e) => e.workOrderId)));
  }

  private async persistDeadLetter(entries: WoOutboxEntry[]): Promise<void> {
    const key = this.deadLetterKey();
    if (!key) {
      return;
    }
    await Preferences.set({ key, value: JSON.stringify(entries) });
    this.deadLetterWorkOrderIds.set(new Set(entries.map((e) => e.workOrderId)));
  }

  private outboxKey(): string | null {
    const userId = this.auth.snapshot?.userId;
    return userId ? `${OUTBOX_KEY_PREFIX}${userId}` : null;
  }

  private deadLetterKey(): string | null {
    const userId = this.auth.snapshot?.userId;
    return userId ? `${DEAD_LETTER_KEY_PREFIX}${userId}` : null;
  }

  private requireUserId(): string {
    const userId = this.auth.snapshot?.userId;
    if (!userId) {
      throw new Error('Sign in before queuing an offline completion.');
    }
    return userId;
  }
}

/** Left unread on purpose — a shared-device leftover must not flush under the next session. */
export const WO_OFFLINE_LEGACY_OUTBOX_KEY = LEGACY_OUTBOX_KEY;
